import React, { useState } from 'react';

/**
 * OverwolfAuthTest Component
 * 
 * This is a development/testing component to simulate the Overwolf authentication flow.
 * It helps developers test the browser-based login without needing the actual Overwolf app.
 * 
 * Usage:
 * 1. Import this component in your App.js
 * 2. Add a route: <Route path="/test-overwolf" element={<OverwolfAuthTest />} />
 * 3. Navigate to /test-overwolf in your browser
 * 4. Click "Simulate Overwolf Login" to test the flow
 */
function OverwolfAuthTest() {
  const [state, setState] = useState('');
  const [polling, setPolling] = useState(false);
  const [authResult, setAuthResult] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] ${message}`);
  };

  const generateState = () => {
    const randomId = Math.random().toString(36).substring(2, 15);
    return `overwolf_${randomId}`;
  };

  const simulateOverwolfLogin = () => {
    const newState = generateState();
    setState(newState);
    addLog(`Generated state: ${newState}`, 'success');
    
    // Open the login page with state parameter
    const loginUrl = `${window.location.origin}/?state=${newState}`;
    addLog(`Opening login page: ${loginUrl}`, 'info');
    window.open(loginUrl, 'OverwolfAuth', 'width=600,height=700');
    
    // Start polling
    startPolling(newState);
  };

  const startPolling = (stateToCheck) => {
    setPolling(true);
    setAuthResult(null);
    setError(null);
    addLog('Started polling for authentication result...', 'info');

    let pollCount = 0;
    const maxPolls = 150; // 5 minutes at 2-second intervals
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      addLog(`Poll attempt ${pollCount}/${maxPolls}`, 'info');

      try {
        const response = await fetch(
          `https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1/auth/status?state=${stateToCheck}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          addLog('Authentication successful! Received token.', 'success');
          setAuthResult(data);
          setPolling(false);
          clearInterval(pollInterval);
        } else if (response.status === 404) {
          addLog('Waiting for user to complete authentication...', 'info');
        } else {
          throw new Error(`Unexpected response: ${response.status}`);
        }
      } catch (err) {
        addLog(`Polling error: ${err.message}`, 'error');
        if (pollCount >= maxPolls) {
          setError('Authentication timeout. Please try again.');
          setPolling(false);
          clearInterval(pollInterval);
        }
      }

      if (pollCount >= maxPolls) {
        addLog('Maximum polling attempts reached. Timeout.', 'error');
        setError('Authentication timeout after 5 minutes.');
        setPolling(false);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      padding: '2rem',
      color: '#e5e7eb'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#f3f4f6' }}>
          🧪 Overwolf Authentication Test
        </h1>
        
        <p style={{ marginBottom: '2rem', color: '#9ca3af' }}>
          This tool simulates the Overwolf desktop app authentication flow for testing purposes.
        </p>

        {/* Control Panel */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Control Panel</h2>
          
          <button
            onClick={simulateOverwolfLogin}
            disabled={polling}
            style={{
              background: polling ? '#6b7280' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              cursor: polling ? 'not-allowed' : 'pointer',
              marginRight: '1rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            {polling ? '🔄 Polling...' : '🚀 Simulate Overwolf Login'}
          </button>

          <button
            onClick={clearLogs}
            style={{
              background: 'rgba(100, 116, 139, 0.5)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Clear Logs
          </button>

          {state && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '6px' }}>
              <strong>Current State:</strong> <code style={{ color: '#60a5fa' }}>{state}</code>
            </div>
          )}
        </div>

        {/* Results Panel */}
        {authResult && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            border: '1px solid rgba(34, 197, 94, 0.3)'
          }}>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#4ade80' }}>
              ✅ Authentication Successful!
            </h2>
            <div style={{ background: 'rgba(0, 0, 0, 0.2)', padding: '1rem', borderRadius: '6px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                {JSON.stringify(authResult, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#f87171'
          }}>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>❌ Error</h2>
            <p>{error}</p>
          </div>
        )}

        {/* Logs Panel */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>📋 Activity Logs</h2>
          
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '1rem',
            borderRadius: '6px',
            maxHeight: '400px',
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          }}>
            {logs.length === 0 ? (
              <p style={{ color: '#6b7280', margin: 0 }}>No logs yet. Click "Simulate Overwolf Login" to start.</p>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '0.5rem',
                    color: log.type === 'error' ? '#f87171' : log.type === 'success' ? '#4ade80' : '#9ca3af'
                  }}
                >
                  <span style={{ color: '#6b7280' }}>[{log.timestamp}]</span> {log.message}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Instructions */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'rgba(59, 130, 246, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)'
        }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#60a5fa' }}>
            ℹ️ How to Use
          </h3>
          <ol style={{ lineHeight: '1.75', color: '#9ca3af', marginLeft: '1.25rem' }}>
            <li>Click "Simulate Overwolf Login" button</li>
            <li>A new browser window will open with the login page</li>
            <li>Sign in with your Google account</li>
            <li>The window will show a success message</li>
            <li>This page will automatically receive the authentication result</li>
            <li>Check the logs and results panels for details</li>
          </ol>
          
          <p style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
            <strong>Note:</strong> This test page simulates what the actual Overwolf desktop app does.
            The polling mechanism checks the backend every 2 seconds for up to 5 minutes.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OverwolfAuthTest;


